import React, { useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import ArticleCard from './ArticleCard';
import AdSlot from './AdSlot';
import { Eye, TrendingUp, Mail, Play, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const TimesBrandsWidget = React.lazy(() => import('./TimesBrandsWidget'));

const API = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar({ recentArticles = [], popularArticles = [], tags = [] }) {
  const { lang, t } = useLanguage();
  const [nlEmail, setNlEmail] = useState('');
  const [nlSubmitted, setNlSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!nlEmail || !nlEmail.includes('@')) return;
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email: nlEmail });
      setNlSubmitted(true);
      setNlEmail('');
    } catch {}
  };

  const labels = {
    hr: { latest: 'Najnovije', popular: 'Popularno', topics: 'Teme', views: 'pregleda', newsletter: 'Newsletter', newsletterDesc: 'Primajte vijesti Croatia Timesa u svoj inbox.', emailPlaceholder: 'Va\u0161a email adresa', subscribe: 'Pretplatite se', tags: 'Oznake' },
    en: { latest: 'Latest', popular: 'Popular', topics: 'Topics', views: 'views', newsletter: 'Newsletter', newsletterDesc: 'Get Croatia Times news in your inbox.', emailPlaceholder: 'Your email', subscribe: 'Subscribe', tags: 'Tags' },
    de: { latest: 'Neueste', popular: 'Beliebt', topics: 'Themen', views: 'Aufrufe', newsletter: 'Newsletter', newsletterDesc: 'Erhalten Sie Croatia Times Nachrichten.', emailPlaceholder: 'Ihre E-Mail', subscribe: 'Abonnieren', tags: 'Schlagw\u00f6rter' },
  };
  const lb = labels[lang] || labels['hr'];

  return (
    <aside className="space-y-5" data-testid="sidebar">
      <AdSlot width="300" height="250" slot="sidebar-category-top" />

      {/* Latest News */}
      <div className="bg-white dark:bg-navy-light rounded-2xl p-4 border border-gray-100 dark:border-white/5">
        <h3 className="font-display text-sm font-bold text-navy dark:text-white mb-4 pb-2 border-b border-sky-accent/30 uppercase tracking-wider flex items-center gap-2">
          <span className="w-1 h-4 bg-sky-accent rounded-full" />
          {lb.latest}
        </h3>
        <div className="space-y-3 mt-3">
          {recentArticles.slice(0, 5).map(article => (
            <ArticleCard key={article.id || article._id} article={article} horizontal />
          ))}
          {recentArticles.length === 0 && (
            <div className="space-y-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="skeleton w-24 h-16 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-3 w-full rounded" />
                    <div className="skeleton h-3 w-3/4 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Most Popular */}
      <div className="bg-white dark:bg-navy-light rounded-2xl p-4 border border-gray-100 dark:border-white/5">
        <h3 className="font-display text-sm font-bold text-navy dark:text-white mb-4 pb-2 border-b border-sky-accent/30 uppercase tracking-wider flex items-center gap-2">
          <TrendingUp size={13} className="text-sky-accent" />
          {lb.popular}
        </h3>
        <div className="space-y-2.5 mt-3">
          {popularArticles.slice(0, 5).map((article, index) => (
            <div key={article.id || article._id} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-sky-accent to-indigo-accent text-white rounded flex items-center justify-center text-[10px] font-display font-bold">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <Link to={`/article/${article.slug}`} className="text-[13px] font-display font-medium text-navy dark:text-white hover:text-sky-accent transition-colors line-clamp-2 leading-snug">
                  {article.title}
                </Link>
                <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
                  <Eye size={9} />
                  <span>{article.view_count || 0} {lb.views}</span>
                </div>
              </div>
            </div>
          ))}
          {popularArticles.length === 0 && (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="skeleton w-6 h-6 rounded flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="skeleton h-3 w-full rounded" />
                    <div className="skeleton h-2.5 w-2/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tag Cloud */}
      {tags.length > 0 && (
        <div className="bg-white dark:bg-navy-light rounded-2xl p-4 border border-gray-100 dark:border-white/5">
          <h3 className="font-display text-sm font-bold text-navy dark:text-white mb-3 uppercase tracking-wider">{lb.tags || lb.topics}</h3>
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 20).map(tag => (
              <Link
                key={tag.name || tag}
                to={`/tag/${encodeURIComponent(tag.name || tag)}`}
                className="px-2.5 py-1 bg-gray-50 dark:bg-white/5 text-navy dark:text-gray-300 text-[10px] font-display font-medium rounded-full hover:bg-sky-accent hover:text-white transition-colors"
              >
                {tag.name || tag}
                {tag.count && <span className="ml-1 text-gray-400">({tag.count})</span>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Times Brands Widget - Compact */}
      <Suspense fallback={null}>
        <TimesBrandsWidget variant="compact" />
      </Suspense>

      {/* Newsletter */}
      <div className="bg-gradient-to-br from-navy to-navy-light rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={14} className="text-sky-accent" />
          <h3 className="font-display text-sm font-bold uppercase tracking-wider">{lb.newsletter}</h3>
        </div>
        {nlSubmitted ? (
          <div className="text-center py-3">
            <Check size={20} className="mx-auto text-green-400 mb-1" />
            <p className="text-sm font-semibold">Hvala vam na pretplati!</p>
            <p className="text-xs text-gray-300 mt-1">Hvala vam što gradite zajednicu s nama.</p>
          </div>
        ) : (
          <>
            <p className="text-gray-300 text-xs mb-3">{lb.newsletterDesc}</p>
            <form className="flex flex-col gap-2" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                value={nlEmail}
                onChange={e => setNlEmail(e.target.value)}
                placeholder={lb.emailPlaceholder}
                required
                className="px-3 py-2 rounded-lg text-xs bg-white/10 border border-white/15 text-white placeholder-gray-400 focus:outline-none focus:border-sky-accent font-display"
              />
              <button type="submit" className="px-4 py-2 bg-sky-accent hover:bg-sky-accent-dark text-white rounded-lg text-xs font-display font-semibold transition-colors">
                {lb.subscribe}
              </button>
            </form>
          </>
        )}
      </div>

      <AdSlot width="300" height="250" slot="sidebar-category-bottom" />
    </aside>
  );
}
